import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { IRootScopeService } from 'angular';
import { find } from 'lodash';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ImUser } from 'Src/ng2/shared/services/im-models/im-user';
import { IUser } from 'Src/ng2/shared/typings/interfaces/user.interface';
import { ListDisplay } from '../list-display.component';
import { ListDisplayService } from '../list-display.service';
import { COLUMN_DATA_TYPE } from './../../../../shared/constants/list-view/cell-type.constant';
import { ModalsService } from './../../../../shared/modals/modals.service';
import { ObjectCache } from './../../../../shared/services/object-cache/object-cache.service';
import { IStudentActivity } from './../../../../shared/typings/interfaces/student-activity.interface';
import { IStudent } from './../../../../shared/typings/interfaces/student.interface';
import { UpdateStudentActivity } from './../../../../store/actions/student-activities-actions';

export const STUDENT_ACTIVITIES_LIST_COLUMNS = [
  {
    human: 'Activities list',
    key: 'activityName',
    cellType: COLUMN_DATA_TYPE.SECTION_HEADER,
    cellConfig: {},
    dataType: null,
    orderBy: null,
  },
  {
    human: 'Hours',
    key: 'hours',
    cellType: COLUMN_DATA_TYPE.GENERIC,
    cellConfig: {},
    dataType: null,
    orderBy: null,
  },
  {
    human: 'Status',
    key: 'status',
    cellType: COLUMN_DATA_TYPE.STUDENT_ACTIVITY_STATUS,
    cellConfig: {},
    dataType: null,
    orderBy: null,
  },
];

interface IStudentActivityRow {
  status: {
    dependencies: {
      studentActivityId: string;
    };
  };
}

@Component({
  selector: 'list-display-student-activities',
  template: require('./list-display-student-activities.component.html'),
  styles: [require('./list-display-student-activities.scss')],
})
export class ListDisplayStudentActivities extends ListDisplay implements OnInit, OnDestroy {
  @Input() currentUser: IUser;
  @Input() student: IStudent;
  @Input() studentActivities: IStudentActivity[];
  @Input() currentSchoolYear: string;

  private isViewOnlyUser: boolean;
  private isEditingUser: boolean;
  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    protected listViewService: ListDisplayService,
    protected objectCache: ObjectCache,
    @Inject('$state') protected $state: any,
    private ngrxStore: Store<any>,
    private modalsService: ModalsService,
    @Inject('$rootScope') private $rootScope: IRootScopeService,
    @Inject('$mdToast') private $mdToast,
    private imUser: ImUser,
  ) {
    super(listViewService, objectCache, $state, ngrxStore);
  }

  ngOnInit(): void {
    this.isViewOnlyUser = this.imUser.isViewType(this.currentUser);
    this.isEditingUser = this.imUser.isEditingUser(this.currentUser);
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  /* istanbul ignore next */
  rowClick(dataRow): void {
    //
  }

  updateStudentActivity(sectionIdx, rowIdx: number): void {
    const studentActivity = this._getStudentActivityById(sectionIdx, rowIdx);

    this.modalsService
      .openStudentActivitiesModal({
        mode: 'UPDATE',
        schoolId: this.student.schoolId,
        studentIds: [this.student._id],
        isProfileMode: true,
        studentActivity,
        currentUser: this.currentUser,
        currentSchoolYear: this.currentSchoolYear,
      })
      .afterClosed()
      .takeUntil(this.destroy$)
      .subscribe();
  }

  unassignStudentActivity(sectionIdx, rowIdx: number): void {
    const studentActivity = this._getStudentActivityById(sectionIdx, rowIdx);
    const studentFullName = this._getStudentFullName();

    this.modalsService
      .openStudentActivityStatusModal({
        mode: 'UNASSIGN',
        isProfileMode: true,
        studentActivity,
        studentFullName,
      })
      .afterClosed()
      .subscribe((payload: { studentActivityId: string; patch: { status: string } }) => {
        if (payload) this.showToast();
      });
  }

  _getStudentActivityById(sectionIdx, rowIdx: number): IStudentActivity {
    // get _id for selected student activity
    const row = this.formattedList.sections[sectionIdx].data[rowIdx] as IStudentActivityRow;
    const studentActivityId = row.status.dependencies.studentActivityId;

    // get studentActivity from store
    const studentActivity = find(this.studentActivities, (studAct: IStudentActivity) => {
      return studAct._id === studentActivityId;
    });
    return studentActivity;
  }

  _getStudentFullName(): string {
    return this.student.studentDetails.name.first + ' ' + this.student.studentDetails.name.last;
  }

  public showToast(): void {
    let toastScope: any = this.$rootScope.$new();
    toastScope.message = 'The activity was updated.';
    toastScope.mdToastAction = null;
    this.$mdToast.show({
      hideDelay: 2000,
      position: 'bottom left',
      template: require('../../../../../app/student/stateless-components/stale-student-toast/stale-student-toast.html'),
      scope: toastScope,
    });
  }
}
